import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  Slider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Key as KeyIcon,
  Warning as WarningIcon,
  CheckCircle as ValidIcon,
  Cancel as ExpiredIcon,
} from '@mui/icons-material';
import api from '../services/api';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';

function LicenseGeneratorPage() {
  const [tenants, setTenants] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  // Snapshot of the edit form as opened, so an update sends only what changed.
  const [editBaseline, setEditBaseline] = useState(null);
  const [selectedTierInfo, setSelectedTierInfo] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    tenant_id: '',
    expiry_days: 365,
    max_users: 5,
    max_agents: 10,
    max_knowledge_pages: 5000,
    notes: '',
  });

  // Auto-fill form when tenant is selected
  const handleTenantSelect = async (tenantId) => {
    const tenant = tenantsWithoutLicense.find(t => t.id === tenantId);
    const updates = { tenant_id: tenantId };
    setSelectedTierInfo(null);
    if (tenant?.subscription_tier) {
      try {
        const res = await api.get(`/license/tier-defaults/${tenant.subscription_tier}`);
        const defaults = res.data;
        updates.max_users = defaults.max_users;
        updates.max_agents = defaults.max_agents;
        updates.max_knowledge_pages = defaults.max_knowledge_pages;
        if (defaults.trial_days > 0) {
          updates.expiry_days = defaults.trial_days;
        } else {
          updates.expiry_days = 365;
        }
        setSelectedTierInfo({ tier: tenant.subscription_tier, ...defaults });
      } catch (err) {
        console.error('Failed to load tier defaults', err);
      }
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tenantsRes, licensesRes] = await Promise.all([
        api.get('/license/tenants'),
        api.get('/license/all'),
      ]);
      setTenants(tenantsRes.data);
      setLicenses(licensesRes.data);
    } catch (err) {
      setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLicense = async () => {
    setError(null);
    try {
      const response = await api.post('/license/generate', formData);
      setSuccess(`License generated successfully for ${response.data.tenant_name}`);
      setGenerateDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate license');
    }
  };

  const handleUpdateLicense = async () => {
    setError(null);
    try {
      // Send only what the superadmin actually changed. The backend turns a
      // submitted max_users/max_agents into a per-tenant limit_overrides entry,
      // so resubmitting an untouched value pins a bespoke override at whatever
      // the tier default happened to be when the licence was issued. Extending
      // an old Scale licence used to write max_agents = 50 and silently restore
      // a cap the catalogue had since lowered to 15.
      const updateData = {};
      ['expiry_days', 'max_users', 'max_agents', 'max_knowledge_pages', 'is_active', 'notes']
        .forEach((key) => {
          if (!editBaseline || formData[key] !== editBaseline[key]) {
            updateData[key] = formData[key];
          }
        });
      if (Object.keys(updateData).length === 0) {
        setSuccess('No changes to save.');
        setEditDialogOpen(false);
        resetForm();
        return;
      }
      const response = await api.put(`/license/tenant/${selectedLicense.tenant_id}`, updateData);
      setSuccess(`License updated successfully for ${response.data.tenant_name}`);
      setEditDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update license');
    }
  };

  const handleDeleteLicense = async () => {
    setError(null);
    try {
      await api.delete(`/license/tenant/${selectedLicense.tenant_id}`);
      setSuccess('License deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedLicense(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete license');
    }
  };

  const resetForm = () => {
    setFormData({
      tenant_id: '',
      expiry_days: 365,
      max_users: 5,
      max_agents: 10,
      max_knowledge_pages: 5000,
      notes: '',
    });
    setSelectedLicense(null);
  };

  const openEditDialog = (license) => {
    setSelectedLicense(license);
    const initial = {
      tenant_id: license.tenant_id,
      expiry_days: license.days_remaining > 0 ? license.days_remaining : 365,
      max_users: license.max_users,
      max_agents: license.max_agents,
      max_knowledge_pages: license.max_knowledge_pages,
      is_active: license.is_active,
      notes: license.notes || '',
    };
    setFormData(initial);
    // Kept so the update can send only what actually changed. expiry_days is
    // derived rather than copied from the licence, so comparing against the
    // licence row itself would not work.
    setEditBaseline(initial);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (license) => {
    setSelectedLicense(license);
    setDeleteDialogOpen(true);
  };

  // Get tenants without licenses for the dropdown
  const tenantsWithoutLicense = tenants.filter(
    t => !licenses.some(l => l.tenant_id === t.id)
  );

  const filteredLicenses = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return licenses.filter((license) => {
      const matchesSearch = !q || [
        license.tenant_name,
        license.company_name,
        license.license_key,
        license.notes,
        license.tenant_id,
      ].some((value) => (value || '').toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === 'all'
        || (statusFilter === 'expired' && (license.is_expired || license.days_remaining <= 0))
        || (statusFilter === 'inactive' && !license.is_active)
        || (statusFilter === 'expiring' && license.is_active && !license.is_expired && license.days_remaining <= 30)
        || (statusFilter === 'valid' && license.is_valid);

      return matchesSearch && matchesStatus;
    });
  }, [licenses, searchTerm, statusFilter]);

  const getLicenseStatusChip = (license) => {
    if (!license.is_active) {
      return <Chip label="Deactivated" color="default" size="small" icon={<ExpiredIcon />} />;
    }
    if (license.is_expired) {
      return <Chip label="Expired" color="error" size="small" icon={<ExpiredIcon />} />;
    }
    if (license.days_remaining <= 30) {
      return <Chip label={`Expires in ${license.days_remaining} days`} color="warning" size="small" icon={<WarningIcon />} />;
    }
    return <Chip label="Valid" color="success" size="small" icon={<ValidIcon />} />;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" mb={3} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
        <Typography variant="h4" component="h1">
          <KeyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          License Manager
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setGenerateDialogOpen(true)}
            disabled={tenantsWithoutLicense.length === 0}
          >
            Generate License
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Licenses</Typography>
              <Typography variant="h4">{licenses.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Active</Typography>
              <Typography variant="h4" color="success.main">
                {licenses.filter(l => l.is_valid).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Expiring Soon</Typography>
              <Typography variant="h4" color="warning.main">
                {licenses.filter(l => l.is_active && !l.is_expired && l.days_remaining <= 30).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Expired/Inactive</Typography>
              <Typography variant="h4" color="error.main">
                {licenses.filter(l => l.is_expired || !l.is_active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' } }}>
          <TextField
            label="Search licenses"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Tenant, company, license key, notes..."
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="expiring">Expiring Soon</MenuItem>
              <MenuItem value="valid">Valid</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant={statusFilter === 'expired' ? 'contained' : 'outlined'}
            color="error"
            onClick={() => setStatusFilter(statusFilter === 'expired' ? 'all' : 'expired')}
          >
            Expired
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
            }}
          >
            Clear
          </Button>
        </Box>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          Showing {filteredLicenses.length} of {licenses.length} licenses
        </Typography>
      </Paper>

      {/* Licenses Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>License Key</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Limits</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLicenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary" py={4}>
                    {licenses.length === 0
                      ? 'No licenses found. Generate a license for a tenant to get started.'
                      : 'No licenses match the current search or filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLicenses.map((license) => (
                <TableRow key={license.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{license.tenant_name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {license.company_name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Tooltip title="Click to copy">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main' }
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(license.license_key);
                          setSuccess('License key copied to clipboard');
                        }}
                      >
                        {license.license_key.substring(0, 16)}...
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{getLicenseStatusChip(license)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(license.expiry_date).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {license.days_remaining > 0 
                        ? `${license.days_remaining} days left`
                        : `Expired ${Math.abs(license.days_remaining)} days ago`
                      }
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="caption" display="block">
                      Users: {license.max_users} | Agents: {license.max_agents}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Pages: {license.max_knowledge_pages?.toLocaleString?.() ?? '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit License">
                      <IconButton size="small" onClick={() => openEditDialog(license)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete License">
                      <IconButton size="small" color="error" onClick={() => openDeleteDialog(license)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Generate License Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New License</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Select Tenant</InputLabel>
            <Select
              value={formData.tenant_id}
              label="Select Tenant"
              onChange={(e) => handleTenantSelect(e.target.value)}
            >
              {tenantsWithoutLicense.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.slug}) — {(tenant.subscription_tier || 'unknown').charAt(0).toUpperCase() + (tenant.subscription_tier || 'unknown').slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedTierInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {selectedTierInfo.tier.charAt(0).toUpperCase() + selectedTierInfo.tier.slice(1)} Tier Includes:
              </Typography>
              <Typography variant="body2">
                {(selectedTierInfo.max_ai_answers_inr ?? selectedTierInfo.max_ai_answers)?.toLocaleString()} INR / {selectedTierInfo.max_ai_answers?.toLocaleString()} USD AI answers/mo · {selectedTierInfo.max_websites} website{selectedTierInfo.max_websites !== 1 ? 's' : ''} · {selectedTierInfo.max_scrape_pages} web pages · {selectedTierInfo.included_notification_phones} WhatsApp · {selectedTierInfo.included_notification_emails} email notifications
              </Typography>
            </Alert>
          )}

          <Typography gutterBottom>License Duration: {formData.expiry_days} days</Typography>
          <Slider
            value={formData.expiry_days}
            onChange={(e, value) => setFormData({ ...formData, expiry_days: value })}
            min={30}
            max={1825}
            step={30}
            marks={[
              { value: 30, label: '30d' },
              { value: 365, label: '1yr' },
              { value: 730, label: '2yr' },
              { value: 1825, label: '5yr' },
            ]}
            sx={{ mb: 3 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Max Users"
                type="number"
                fullWidth
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1, max: 10000 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Max Agents"
                type="number"
                fullWidth
                value={formData.max_agents}
                onChange={(e) => setFormData({ ...formData, max_agents: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1, max: 1000 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Max Knowledge Pages"
                type="number"
                fullWidth
                value={formData.max_knowledge_pages}
                onChange={(e) => setFormData({ ...formData, max_knowledge_pages: parseInt(e.target.value) || 500 })}
                inputProps={{ min: 100, max: 1000000 }}
              />
            </Grid>
          </Grid>

          <TextField
            label="Notes"
            multiline
            rows={2}
            fullWidth
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGenerateDialogOpen(false); resetForm(); }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleGenerateLicense}
            disabled={!formData.tenant_id}
          >
            Generate License
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit License Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit License - {selectedLicense?.tenant_name}
        </DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label="License Active"
            sx={{ mt: 2, mb: 2 }}
          />

          <Typography gutterBottom>Extend License: {formData.expiry_days} days from today</Typography>
          <Slider
            value={formData.expiry_days}
            onChange={(e, value) => setFormData({ ...formData, expiry_days: value })}
            min={30}
            max={1825}
            step={30}
            marks={[
              { value: 30, label: '30d' },
              { value: 365, label: '1yr' },
              { value: 730, label: '2yr' },
              { value: 1825, label: '5yr' },
            ]}
            sx={{ mb: 3 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Max Users"
                type="number"
                fullWidth
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1, max: 10000 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Max Agents"
                type="number"
                fullWidth
                value={formData.max_agents}
                onChange={(e) => setFormData({ ...formData, max_agents: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1, max: 1000 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Max Knowledge Pages"
                type="number"
                fullWidth
                value={formData.max_knowledge_pages}
                onChange={(e) => setFormData({ ...formData, max_knowledge_pages: parseInt(e.target.value) || 500 })}
                inputProps={{ min: 100, max: 1000000 }}
              />
            </Grid>
          </Grid>

          <TextField
            label="Notes"
            multiline
            rows={2}
            fullWidth
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); resetForm(); }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateLicense}>
            Update License
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteLicense}
        title="Delete License"
        entityName={selectedLicense?.tenant_name || ''}
        warningText="This will prevent users in this tenant from logging in until a new license is generated."
        confirmButtonText="Delete License"
      />
    </Container>
  );
}

export default LicenseGeneratorPage;
